import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";

export function defaultLabelDate() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function escapeHTML(value) {
    return String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}

function formatDateFR(isoDate) {
    if (!isoDate) return "";
    const [year, month, day] = isoDate.split("-");
    if (!year || !month || !day) return escapeHTML(isoDate);
    return `${day}.${month}.${year.slice(2)}`;
}

function normalizeBoxCount(value) {
    const numeric = Number.parseInt(value, 10);
    if (!Number.isFinite(numeric)) return 1;
    return Math.max(1, Math.min(40, numeric));
}

function resolvePharmacyName(pharmacy) {
    return pharmacy?.name || pharmacy?.nom || "Pharmacie";
}

function hasPharmacyIdentity(pharmacy) {
    const name = pharmacy?.name || pharmacy?.nom;
    return typeof name === "string" && name.trim().length > 0;
}

function codeFontSize(codeLength) {
    const sizes = { 1: 52, 2: 50, 3: 44, 4: 34, 5: 27, 6: 22, 7: 18, 8: 16 };
    return `${sizes[codeLength] || 14}mm`;
}

function previewCodeFontSize(codeLength) {
    const sizes = { 1: 118, 2: 106, 3: 84, 4: 66, 5: 54, 6: 46, 7: 38, 8: 34 };
    return `${sizes[codeLength] || 26}px`;
}

function gaugeClass(position, current) {
    if (position === current) return "active";
    if (position < current) return "faite";
    return "";
}

function printButtonLabel(saving, pharmacyReady) {
    if (saving) return "Préparation…";
    if (pharmacyReady) return "Imprimer";
    return "Pharmacie en cours de chargement…";
}

export function LabelPreview({ ehpad, pharmacy, date, boxCount = 1, code, scale = 1 }) {
    const safeCode = (code || ehpad?.ehpadCode || ehpad?.ehpadNom || "-").trim().toUpperCase();
    const total = normalizeBoxCount(boxCount);
    const pharmacyName = resolvePharmacyName(pharmacy);
    const pharmacyCity = pharmacy?.city || "";
    const width = 220;
    const height = 330;
    const gauge = total > 1 && total <= 14
        ? Array.from({ length: total }, (_unused, index) => (
            <span
                key={index}
                style={{
                    width: "8px",
                    height: "8px",
                    border: "1.5px solid #111",
                    borderRadius: "0",
                    background: index === 0 ? "#111" : "transparent",
                    transform: index === 0 ? "scale(1.18)" : "none",
                }}
            />
        ))
        : null;

    return (
        <div style={{
            width: `${width * scale}px`,
            height: `${height * scale}px`,
            position: "relative",
            flexShrink: 0,
        }}>
            <div style={{
            width: `${width}px`,
            height: `${height}px`,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            background: "white",
            color: "#111",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            border: "2px solid #111",
            boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
            fontFamily: '"Signika","Segoe UI",Arial,sans-serif',
        }}>
            <div style={{ borderBottom: "2px solid #111", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 12px" }}>
                <div style={{ fontSize: "19px", fontWeight: 900, letterSpacing: ".14em" }}>NEOPHARM</div>
                <div style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "7px", fontWeight: 900, letterSpacing: ".06em", textTransform: "uppercase", color: "#111" }}>
                    <img src="/symbole-couleur.svg" alt="" style={{ width: "18px", height: "18px", filter: "grayscale(1) contrast(1.5)" }} />
                    <span>Suivi qualité</span>
                </div>
            </div>
            <div style={{ textAlign: "center", padding: "12px 12px 0" }}>
                <div style={{ fontSize: "11px", fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", lineHeight: 1.15 }}>
                    {pharmacyName}
                </div>
                {pharmacyCity && (
                    <div style={{ marginTop: "3px", fontSize: "8px", letterSpacing: ".16em", textTransform: "uppercase" }}>
                        {pharmacyCity}
                    </div>
                )}
            </div>
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "8px 12px" }}>
                <div style={{ marginBottom: "4px", fontSize: "8px", fontWeight: 800, letterSpacing: ".24em", textTransform: "uppercase", opacity: .75 }}>
                    Destination
                </div>
                <div style={{ width: "100%", maxWidth: "196px", overflow: "hidden", textAlign: "center", fontSize: previewCodeFontSize(safeCode.length), lineHeight: .9, fontWeight: 800, letterSpacing: 0 }}>
                    {safeCode}
                </div>
                {gauge && <div style={{ display: "flex", gap: "4px", justifyContent: "center", flexWrap: "wrap", marginTop: "12px", maxWidth: "180px" }}>{gauge}</div>}
            </div>
            <div style={{ display: "flex", gap: "10px", alignItems: "flex-end", padding: "0 14px 11px" }}>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "8px", fontWeight: 800, letterSpacing: ".2em", textTransform: "uppercase", opacity: .75 }}>Scellé le</div>
                    <div style={{ marginTop: "3px", fontSize: "24px", fontWeight: 800, lineHeight: 1 }}>{formatDateFR(date || defaultLabelDate())}</div>
                </div>
                <div style={{ flex: "0 0 78px", borderLeft: "3px solid #111", paddingLeft: "10px" }}>
                    <div style={{ fontSize: "8px", fontWeight: 800, letterSpacing: ".2em", textTransform: "uppercase", opacity: .75 }}>Caisse</div>
                    <div style={{ display: "flex", alignItems: "baseline", fontSize: "48px", fontWeight: 800, lineHeight: .9 }}>
                        1<span style={{ margin: "0 3px", color: "#111", fontSize: "30px", fontWeight: 400 }}>/</span><span style={{ fontSize: "28px", fontWeight: 400 }}>{total}</span>
                    </div>
                </div>
            </div>
            <div style={{ borderTop: "2px solid #111", color: "#111", textAlign: "center", padding: "10px", fontSize: "11px", fontWeight: 900, letterSpacing: ".1em", textTransform: "uppercase" }}>
                Livraison de médicament
            </div>
            </div>
        </div>
    );
}

export function generateLabelPrintHTML({ ehpad, pharmacy, date, boxCount }) {
    const code = (ehpad?.ehpadCode || ehpad?.ehpadNom || "-").trim().toUpperCase();
    const total = normalizeBoxCount(boxCount);
    const dateLabel = formatDateFR(date || defaultLabelDate());
    const pharmacyName = resolvePharmacyName(pharmacy);
    const pharmacyCity = pharmacy?.city || "";

    const labels = Array.from({ length: total }, (_, index) => {
        const current = index + 1;
        const gauge = total > 1 && total <= 14
            ? `<div class="jauge">${Array.from({ length: total }, (_unused, gaugeIndex) => {
                const n = gaugeIndex + 1;
                return `<span class="case ${gaugeClass(n, current)}"></span>`;
            }).join("")}</div>`
            : "";

        return `<section class="etiquette">
            <div class="entete">
                <div class="neopharm-text">NEOPHARM</div>
                <div class="suivi-qualite">
                    <img class="sq-logo" src="/symbole-couleur.svg" alt="">
                    <span>SUIVI QUALITÉ</span>
                </div>
            </div>
            <div class="officine">
                <div class="nom">${escapeHTML(pharmacyName)}</div>
                ${pharmacyCity ? `<div class="ville">${escapeHTML(pharmacyCity)}</div>` : ""}
            </div>
            <div class="bloc-sigle">
                <div class="surtitre">Destination</div>
                <div class="sigle" style="font-size:${codeFontSize(code.length)}">${escapeHTML(code)}</div>
                ${gauge}
            </div>
            <div class="pied-donnees">
                <div class="colonne"><div class="cle">Scellé le</div><div class="valeur-date">${escapeHTML(dateLabel)}</div></div>
                <div class="colonne"><div class="cle">Caisse</div><div class="fraction">${current}<span class="barre">/</span><span class="tot">${total}</span></div></div>
            </div>
            <div class="mention">Livraison de médicament</div>
        </section>`;
    }).join("");

    return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8">
<title>Etiquettes caisses - ${escapeHTML(code)}</title>
<style>
    :root{--ink:#000;--paper:#fff;--muted:#444;--police:"Signika","Segoe UI",Arial,sans-serif}
    *{box-sizing:border-box}
    html,body{margin:0;padding:0;background:#fff}
    body{font-family:var(--police);color:var(--ink)}
    .etiquette{width:100mm;height:150mm;background:var(--paper);color:var(--ink);display:flex;flex-direction:column;overflow:hidden;break-after:page;page-break-after:always;border:0.8mm solid var(--ink)}
    .etiquette:last-child{break-after:auto;page-break-after:auto}
    .entete{height:18mm;border-bottom:0.7mm solid var(--ink);display:flex;align-items:center;justify-content:space-between;padding:0 5mm}
    .neopharm-text{font-weight:900;font-size:7mm;letter-spacing:.18em;line-height:1}
    .suivi-qualite{display:flex;align-items:center;gap:1.6mm;font-size:2.35mm;font-weight:900;letter-spacing:.08em;text-transform:uppercase;white-space:nowrap}
    .suivi-qualite .sq-logo{width:7.4mm;height:7.4mm;display:block;filter:grayscale(1) contrast(1.8)}
    .officine{text-align:center;padding:4mm 6mm 0}
    .officine .nom{font-weight:700;font-size:4.4mm;letter-spacing:.06em;text-transform:uppercase;line-height:1.15}
    .officine .ville{font-weight:400;font-size:2.9mm;letter-spacing:.2em;text-transform:uppercase;margin-top:1.2mm}
    .bloc-sigle{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:2mm 5mm}
    .surtitre{font-weight:600;font-size:2.8mm;letter-spacing:.32em;text-transform:uppercase;margin-bottom:1mm;opacity:.75}
    .sigle{font-weight:700;line-height:.92;text-transform:uppercase;text-align:center;max-width:92mm;overflow:hidden}
    .jauge{display:flex;gap:1.6mm;justify-content:center;margin-top:5mm;flex-wrap:wrap;max-width:88%}
    .case{width:4.6mm;height:4.6mm;border:.7mm solid var(--ink);border-radius:0}
    .case.faite{background:var(--ink);opacity:.35}
    .case.active{background:var(--ink);border-color:var(--ink);transform:scale(1.28);opacity:1}
    .pied-donnees{display:flex;align-items:flex-end;padding:0 6mm 4mm;gap:5mm}
    .colonne{flex:1}
    .colonne+.colonne{border-left:.7mm solid var(--ink);padding-left:5mm;flex:0 0 44%}
    .cle{font-weight:600;font-size:2.7mm;letter-spacing:.24em;text-transform:uppercase;margin-bottom:1.5mm;opacity:.75}
    .valeur-date{font-weight:700;font-size:8.5mm;line-height:1}
    .fraction{font-weight:700;font-size:21mm;line-height:.9;display:flex;align-items:baseline}
    .fraction .barre{font-weight:300;font-size:13mm;margin:0 1mm;color:var(--ink)}
    .fraction .tot{font-weight:300;font-size:12mm}
    .mention{border-top:.7mm solid var(--ink);color:var(--ink);text-align:center;font-weight:900;font-size:5mm;letter-spacing:.12em;text-transform:uppercase;padding:3.6mm 5mm;line-height:1.15}
    @page{size:100mm 150mm;margin:0}
    @media print{*{-webkit-print-color-adjust:exact;print-color-adjust:exact} .suivi-qualite .sq-logo{filter:grayscale(1) contrast(2)}}
</style>
</head>
<body>${labels}</body>
</html>`;
}

export function openLabelPrintWindow(settings) {
    const html = generateLabelPrintHTML(settings);
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = globalThis.open(url, "_blank", "width=520,height=760");
    if (!win) {
        URL.revokeObjectURL(url);
        return;
    }
    win.addEventListener("load", () => {
        URL.revokeObjectURL(url);
        win.focus();
        win.print();
    }, { once: true });
}

const fieldStyle = {
    width: "100%",
    display: "block",
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    borderRadius: "9px",
    padding: "9px 10px",
    color: "#0f172a",
    fontSize: "13px",
    fontWeight: 700,
    background: "white",
};

export function LabelPrintModal({ ehpad, pharmacy, initialBoxCount = 1, onPrintSettingsSave = null, onClose }) {
    const [date, setDate] = useState(defaultLabelDate());
    const [code, setCode] = useState((ehpad?.ehpadCode || "").toUpperCase());
    const [boxCount, setBoxCount] = useState(normalizeBoxCount(initialBoxCount));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const previewCount = useMemo(() => normalizeBoxCount(boxCount), [boxCount]);
    const pharmacyReady = hasPharmacyIdentity(pharmacy);

    const print = async () => {
        if (saving || !pharmacyReady) return;
        setSaving(true);
        setError("");
        try {
            if (onPrintSettingsSave) {
                await onPrintSettingsSave({ boxCount: previewCount, date, code });
            }
            openLabelPrintWindow({
                ehpad: { ...ehpad, ehpadCode: code.trim().toUpperCase() || ehpad?.ehpadCode },
                pharmacy,
                date,
                boxCount: previewCount,
            });
        } catch (err) {
            console.error(err);
            setError("Impossible d'enregistrer le nombre de caisses. Réessayez.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            style={{ position: "fixed", inset: 0, zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
        >
            <button type="button" aria-label="Fermer l'impression d'étiquettes" onClick={onClose} style={{ position: "absolute", inset: 0, border: 0, background: "rgba(15,23,42,.45)" }} />
            <dialog open aria-labelledby="label-print-title" onKeyDown={event => { if (event.key === "Escape") onClose(); }} style={{ position: "relative", margin: 0, padding: 0, width: "100%", maxWidth: "500px", maxHeight: "calc(100vh - 40px)", display: "flex", flexDirection: "column", borderRadius: "14px", background: "white", border: "1px solid #e2e8f0", boxShadow: "0 24px 70px rgba(15,23,42,.22)", overflow: "hidden" }}>
                <div style={{ padding: "20px 22px 12px" }}>
                    <h3 id="label-print-title" style={{ margin: "0 0 4px", color: "#0f172a", fontSize: "17px" }}>Impression d'étiquettes</h3>
                    <p style={{ margin: 0, color: "#64748b", fontSize: "13px", lineHeight: 1.45 }}>
                        {previewCount} étiquette{previewCount > 1 ? "s" : ""} caisse pour {ehpad?.ehpadNom || "cet EHPAD"}.
                    </p>
                    {error && (
                        <p role="alert" style={{ margin: "8px 0 0", color: "#b91c1c", fontSize: "12px", fontWeight: 700 }}>
                            {error}
                        </p>
                    )}
                </div>
                <div style={{ display: "grid", gap: "12px", padding: "0 22px 14px", overflowY: "auto" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 12px", alignContent: "start" }}>
                        <label style={{ display: "block", color: "#475569", fontSize: "13px", fontWeight: 800 }}>
                            <span>Date</span>
                            <input type="date" value={date} onChange={event => setDate(event.target.value)} style={{ ...fieldStyle, marginTop: "6px" }} />
                        </label>
                        <label style={{ display: "block", color: "#475569", fontSize: "13px", fontWeight: 800 }}>
                            <span>Nombre de caisses</span>
                            <input type="number" min="1" max="40" value={boxCount} onChange={event => setBoxCount(event.target.value)} disabled={saving} style={{ ...fieldStyle, width: "110px", marginTop: "6px" }} />
                        </label>
                        <label style={{ display: "block", gridColumn: "1 / -1", color: "#475569", fontSize: "13px", fontWeight: 800 }}>
                            <span>Code EHPAD</span>
                            <input value={code} maxLength={8} onChange={event => setCode(event.target.value.toUpperCase())} style={{ ...fieldStyle, marginTop: "6px" }} />
                        </label>
                    </div>
                    <div style={{ display: "grid", justifyItems: "center", gap: "10px", padding: "12px", borderRadius: "12px", background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                        <LabelPreview ehpad={ehpad} pharmacy={pharmacy} date={date} boxCount={previewCount} code={code} scale={1.12} />
                        <button type="button" onClick={print} disabled={saving || !pharmacyReady} title={!pharmacyReady ? "Chargement des informations de la pharmacie" : undefined} style={{ width: "246px", padding: "10px 14px", borderRadius: "8px", border: "1px solid #0f766e", background: "#ccfbf1", cursor: saving || !pharmacyReady ? "not-allowed" : "pointer", fontSize: "14px", color: "#0f766e", fontWeight: 800, opacity: saving || !pharmacyReady ? 0.6 : 1 }}>
                            {printButtonLabel(saving, pharmacyReady)}
                        </button>
                    </div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", padding: "16px 22px", borderTop: "1px solid #e2e8f0", background: "#f8fafc" }}>
                    <button type="button" onClick={onClose} style={{ padding: "8px 14px", borderRadius: "7px", border: "1px solid #cbd5e1", background: "white", cursor: "pointer", fontSize: "13px", color: "#475569" }}>
                        Fermer
                    </button>
                </div>
            </dialog>
        </div>
    );
}

const ehpadShape = PropTypes.shape({
    ehpadCode: PropTypes.string,
    ehpadNom: PropTypes.string,
});

const pharmacyShape = PropTypes.shape({
    name: PropTypes.string,
    city: PropTypes.string,
});

LabelPrintModal.propTypes = {
    ehpad: ehpadShape,
    pharmacy: pharmacyShape,
    initialBoxCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onPrintSettingsSave: PropTypes.func,
    onClose: PropTypes.func.isRequired,
};

LabelPreview.propTypes = {
    ehpad: ehpadShape,
    pharmacy: pharmacyShape,
    date: PropTypes.string,
    boxCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    code: PropTypes.string,
    scale: PropTypes.number,
};
